import { useState } from 'react';
import { RM_COHORTS, type RmCohort } from '../shared/q3';
import type { Standing } from './standings';
import { COH, StatusTag } from './parts';
import { cr, pc } from './format';
import { Link, to, toList, useNavigate } from './router';

/**
 * Summary categories behind the clickable home tiles and "Who's where" bars.
 * The tests match how those numbers are counted, so a list always has as many
 * names as the tile it was opened from.
 */
export const LIST_CATS: Record<string, { title: string; desc: string; test: (r: Standing, pace: number) => boolean }> = {
  qualified: {
    title: 'Qualified',
    desc: 'Total target met, with the Mandatory minimum inside it.',
    test: (r) => r.qualified,
  },
  'on-pace': {
    title: 'On pace',
    desc: "At or ahead of where today's pace says they should be.",
    test: (r, pace) => r.pct >= pace,
  },
  'past-halfway': {
    title: 'Past halfway',
    desc: 'At 50% or more of target, not yet qualified.',
    test: (r) => r.status === 'In the race' && r.pct >= 0.5,
  },
  'mandatory-short': {
    title: 'Mandatory short',
    desc: 'Total target met, Mandatory minimum still pending.',
    test: (r) => r.status === 'Mandatory gap',
  },
  'under-half': {
    title: 'Under 50%',
    desc: 'Below 50% of target, not yet qualified.',
    test: (r) => r.status === 'In the race' && r.pct < 0.5,
  },
};

interface Props {
  rows: Standing[];
  pace: number;
  cat: string;
  cohort: RmCohort | null;
  meId: string | null;
}

export default function ListView({ rows, pace, cat, cohort, meId }: Props) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const def = LIST_CATS[cat];

  const back = (
    <nav className="sk-mine-nav" aria-label="Back">
      <Link className="sk-back" to={to('home')}>
        ← Overview
      </Link>
    </nav>
  );

  if (!def) {
    return (
      <div className="sk-view sk-listv">
        {back}
        <p className="sk-note" style={{ marginTop: 24 }}>
          That list doesn't exist. Head back to the overview and pick a category.
        </p>
      </div>
    );
  }

  const all = rows
    .filter((r) => (!cohort || r.cohort === cohort) && def.test(r, pace))
    .sort((a, b) => Number(b.qualified) - Number(a.qualified) || b.pct - a.pct);
  const q = search.trim().toLowerCase();
  const list = q ? all.filter((r) => (r.rm + ' ' + r.head + ' ' + r.loc).toLowerCase().includes(q)) : all;
  const open = (r: Standing) => navigate(to('me', r.empId));

  return (
    <div className="sk-view sk-listv">
      {back}
      <div className="sk-h2row sk-mine-h">
        <div>
          <div className="sk-num">{cohort ? COH[cohort].name.toUpperCase() : 'BOTH COHORTS'}</div>
          <h2>{def.title}</h2>
        </div>
        <p className="sk-note">
          {all.length} RM{all.length === 1 ? '' : 's'} · {def.desc}
        </p>
      </div>

      <div className="sk-list-tools">
        <div className="sk-seg" role="group" aria-label="Cohort">
          {[null, ...RM_COHORTS].map((c) => (
            <Link key={c ?? 'all'} to={toList(cat, c)} aria-selected={c === cohort} role="tab">
              {c ? COH[c].name : 'Both'}
            </Link>
          ))}
        </div>
        <input
          className="sk-search"
          type="search"
          placeholder="Search name, P&L head or city"
          aria-label="Search this list"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="sk-board">
        <table className="sk-tbl">
          <thead>
            <tr>
              <th>#</th>
              <th>RM</th>
              <th>Cohort</th>
              <th className="hide-m">P&amp;L head</th>
              <th className="hide-m hide-l">City</th>
              <th className="num">Total</th>
              <th className="num">% of target</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr
                key={r.empId}
                className={'sk-xrow' + (r.empId === meId ? ' me' : '')}
                tabIndex={0}
                title={'Open ' + r.rm + "'s progress"}
                onClick={() => open(r)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    open(r);
                  }
                }}
              >
                <td>
                  <span className="sk-rank">{r.ranked ? r.rank : '–'}</span>
                </td>
                <td className="sk-rm" title={r.rm}>
                  {r.rm}
                  {r.empId === meId && <span className="sk-you">YOU</span>}
                </td>
                <td>
                  <span className={'sk-cohpill ' + COH[r.cohort].cls}>{COH[r.cohort].name}</span>
                </td>
                <td className="hide-m sk-trunc" title={r.head}>
                  {r.head}
                </td>
                <td className="hide-m hide-l sk-trunc" title={r.loc}>
                  {r.loc}
                </td>
                <td className="num">{cr(r.totalCr)}</td>
                <td className="num">{pc(r.pct)}</td>
                <td>
                  <StatusTag r={r} />
                </td>
              </tr>
            ))}
            {!list.length && (
              <tr>
                <td className="sk-empty" colSpan={8}>
                  {all.length ? 'No RMs match your search.' : 'No RMs in this list right now.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="sk-note" style={{ marginTop: 10 }}>
        # is the RM's rank within their cohort. Tap a name to open their progress and product split.
      </p>
    </div>
  );
}
